import { UserScope } from '@logto/core-kit';
import { z } from 'zod';
import { Applications, OrganizationScopes, Resources, Scopes, ApplicationSignInExperiences, } from '../db-entries/index.js';
import { customClientMetadataGuard } from '../foundations/index.js';
/**
 * The application response returned by Management APIs.
 *
 * The legacy client secret is only present before it has been replaced by an internal secret.
 */
export const applicationResponseGuard = Applications.guard.extend({
    secret: Applications.guard.shape.secret.optional(),
});
/** The guard for {@link FeaturedApplication}. */
export const featuredApplicationGuard = Applications.guard.pick({
    id: true,
    name: true,
    type: true,
});
export const applicationCreateGuard = Applications.createGuard
    .omit({
    appLevelAccessControlEnabled: true,
    id: true,
    createdAt: true,
    secret: true,
    tenantId: true,
})
    .partial()
    .merge(Applications.createGuard.pick({ name: true, type: true }))
    .extend({
    /**
     * Strict on the API input: an unknown key (e.g. a typo such as `refreshTokenTtlInDay`) is
     * rejected with a 400 instead of being silently stripped and answered 200. This makes the keys
     * of `customClientMetadataGuard` part of the API contract; see the rules documented there.
     */
    customClientMetadata: customClientMetadataGuard.strict().optional(),
});
export const applicationPatchGuard = applicationCreateGuard.partial().omit({
    type: true,
    isThirdParty: true,
});
const applicationAccessControlRuleLimit = 1000;
const applicationAccessControlRawRuleLimit = applicationAccessControlRuleLimit * 2;
const uniqueStringArrayGuard = z
    .array(z.string())
    .max(applicationAccessControlRawRuleLimit)
    .transform((values) => [...new Set(values)])
    .pipe(z.array(z.string()).max(applicationAccessControlRuleLimit));
/** The guard for one organization role access-control rule group. */
export const applicationAccessControlOrganizationRoleRuleGuard = z.object({
    organizationId: z.string(),
    organizationRoleIds: uniqueStringArrayGuard,
});
/** The guard for application-level access control rule payloads. */
export const applicationAccessControlGuard = z
    .object({
    userIds: uniqueStringArrayGuard,
    userRoleIds: uniqueStringArrayGuard,
    organizationIds: uniqueStringArrayGuard,
    organizationRoleRules: z
        .array(applicationAccessControlOrganizationRoleRuleGuard)
        .max(applicationAccessControlRawRuleLimit),
})
    .transform(({ organizationRoleRules, ...rest }) => {
    const organizationRoleRulesMap = new Map();
    for (const { organizationId, organizationRoleIds } of organizationRoleRules) {
        const roleIds = organizationRoleRulesMap.get(organizationId) ?? new Set();
        for (const roleId of organizationRoleIds) {
            roleIds.add(roleId);
        }
        organizationRoleRulesMap.set(organizationId, roleIds);
    }
    return {
        ...rest,
        organizationRoleRules: [...organizationRoleRulesMap.entries()].map(([organizationId, organizationRoleIds]) => ({
            organizationId,
            organizationRoleIds: [...organizationRoleIds],
        })),
    };
})
    .pipe(z.object({
    userIds: z.array(z.string()).max(applicationAccessControlRuleLimit),
    userRoleIds: z.array(z.string()).max(applicationAccessControlRuleLimit),
    organizationIds: z.array(z.string()).max(applicationAccessControlRuleLimit),
    organizationRoleRules: z
        .array(applicationAccessControlOrganizationRoleRuleGuard)
        .max(applicationAccessControlRuleLimit),
}));
/** Create an empty application-level access control rule set. */
export const createDefaultApplicationAccessControl = () => ({
    userIds: [],
    userRoleIds: [],
    organizationIds: [],
    organizationRoleRules: [],
});
const resourceScopesGuard = z.array(z.object({
    resource: Resources.guard.pick({ id: true, name: true, indicator: true }),
    scopes: z.array(Scopes.guard.pick({ id: true, name: true, description: true })),
}));
export const applicationUserConsentScopesResponseGuard = z.object({
    organizationScopes: z.array(OrganizationScopes.guard.pick({ id: true, name: true, description: true })),
    resourceScopes: resourceScopesGuard,
    organizationResourceScopes: resourceScopesGuard,
    userScopes: z.array(z.nativeEnum(UserScope)),
});
export var ApplicationUserConsentScopeType;
(function (ApplicationUserConsentScopeType) {
    ApplicationUserConsentScopeType["OrganizationScopes"] = "organization-scopes";
    ApplicationUserConsentScopeType["ResourceScopes"] = "resource-scopes";
    ApplicationUserConsentScopeType["OrganizationResourceScopes"] = "organization-resource-scopes";
    ApplicationUserConsentScopeType["UserScopes"] = "user-scopes";
})(ApplicationUserConsentScopeType || (ApplicationUserConsentScopeType = {}));
export const applicationSignInExperienceCreateGuard = ApplicationSignInExperiences.createGuard
    .omit({
    applicationId: true,
    tenantId: true,
    termsOfUseUrl: true,
    privacyPolicyUrl: true,
})
    // Align with the sign-in-experience create guard.
    .merge(z.object({
    termsOfUseUrl: z.string().max(2048).url().optional().nullable().or(z.literal('')),
    privacyPolicyUrl: z.string().max(2048).url().optional().nullable().or(z.literal('')),
}));
